import {
  isCanvasActionCardSlotGeometrySafe,
  type CanvasActionCardSlot,
} from '@littlebird/canvas';
import { getSDK } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { YStack } from '@/components/ui/layout/layout';
import { ActionCardItem } from '@/features/action-cards/components/ActionCardItem';

const CanvasActionCard = observer(
  ({
    chatId,
    localId,
    slotId,
    onHeightChange,
    rowExpansion,
  }: {
    chatId: string;
    localId: number;
    slotId?: string;
    onHeightChange?: (slotId: string, height: number) => void;
    rowExpansion?: {
      expanded: boolean;
      onExpandedChange: (expanded: boolean) => void;
    };
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const card = getSDK().stores.actionCards.getByLocalIdInChat(
      chatId,
      localId
    );

    useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container || !card || !slotId || !onHeightChange) return;

      const reportHeight = () => {
        const height = container.getBoundingClientRect().height;
        if (height > 0) onHeightChange(slotId, height);
      };
      reportHeight();
      if (typeof ResizeObserver === 'undefined') return;

      const observer = new ResizeObserver(reportHeight);
      observer.observe(container);
      return () => observer.disconnect();
    }, [card, onHeightChange, slotId]);

    if (!card) return null;

    return (
      <div ref={containerRef} className="w-full">
        <ActionCardItem
          card={card}
          surface="canvas"
          rowExpansion={rowExpansion}
        />
      </div>
    );
  }
);

export const CanvasActionCardOverlay = ({
  chatId,
  slots,
  frameWidth,
  frameHeight,
  onHeightChange,
}: {
  chatId: string;
  slots: CanvasActionCardSlot[];
  frameWidth: number;
  frameHeight: number;
  onHeightChange: (slotId: string, height: number) => void;
}) => {
  const [expandedSlotIds, setExpandedSlotIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const handleExpandedChange = useCallback(
    (slotId: string, expanded: boolean) => {
      setExpandedSlotIds((current) => {
        if (current.has(slotId) === expanded) return current;

        const next = new Set(current);
        if (expanded) next.add(slotId);
        else next.delete(slotId);
        return next;
      });
    },
    []
  );

  return (
    <div className="pointer-events-none absolute inset-0 z-[1] overflow-hidden">
      {slots.flatMap((slot) => {
        if (
          !isCanvasActionCardSlotGeometrySafe(slot, {
            width: frameWidth,
            height: frameHeight,
          })
        )
          return [];

        return [
          <div
            key={slot.slotId}
            className="pointer-events-auto absolute"
            style={{ top: slot.top, left: slot.left, width: slot.width }}
          >
            <CanvasActionCard
              chatId={chatId}
              localId={slot.localId}
              slotId={slot.slotId}
              onHeightChange={onHeightChange}
              rowExpansion={{
                expanded: expandedSlotIds.has(slot.slotId),
                onExpandedChange: (expanded) =>
                  handleExpandedChange(slot.slotId, expanded),
              }}
            />
          </div>,
        ];
      })}
    </div>
  );
};

export const CanvasActionCardFallback = ({
  chatId,
  localIds,
}: {
  chatId: string;
  localIds: number[];
}) => {
  if (localIds.length === 0) return null;

  return (
    <YStack className="relative z-[1] shrink-0 gap-2 border-t border-border bg-background p-3">
      {localIds.map((localId) => (
        <CanvasActionCard key={localId} chatId={chatId} localId={localId} />
      ))}
    </YStack>
  );
};
